import { formatDate } from '@if/core/utils'
import { Action, ActionList } from '@if/mui'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Receipt } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { isAnalysisActive } from '../../credit-hooks'
import StageContent, { StageField } from './StageContent'

interface Props {
  creditApp: CreditApplication
}

// Funding descriptor for StageContent: contract/payment details + funding summary link.
const FundingStageContent = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const isAnalysing = isAnalysisActive(creditApp)
  const { worksheet, contract } = creditApp

  // Contract + payment breakdown; hidden while the analysis is still running.
  const fields: StageField[] = isAnalysing
    ? []
    : [
        { label: t('worksheet.contractNumber'), value: contract?.cvtNumber ? `#${contract.cvtNumber}` : undefined },
        {
          label: t('viewCreditApplication.activationDate'),
          value: creditApp.deliveryOn ? formatDate(creditApp.deliveryOn) : undefined,
        },
        { label: t('worksheet.paymentAmount'), value: worksheet?.paymentAmountForFrequency },
        {
          label: t('worksheet.paymentFrequency'),
          value: worksheet ? t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' }) : undefined,
        },
        {
          label: t('worksheet.firstPaymentOn'),
          value: worksheet?.firstPaymentOn ? formatDate(worksheet.firstPaymentOn) : undefined,
        },
        { label: t('worksheet.loanTerm'), value: worksheet ? `${worksheet.term} ${t('common.months')}` : undefined },
      ]

  // Enabled only once the summary exists (loan created), regardless of status.
  const viewFundingSummary: Action = {
    icon: Receipt,
    label: t('viewCreditApplication.printFundingSummary'),
    href: `/credits/${creditApp.financingProgramId}/${creditApp.id}/funding-summary`,
    disabled: !creditApp.loanCreated,
  }

  return (
    <StageContent
      fields={fields}
      columns={3}
      loading={isAnalysing}
      actions={
        <ActionList actions={[viewFundingSummary]} variant="button" menuLabel={t('viewCreditApplication.actions')} />
      }
    />
  )
}

export default FundingStageContent
